// New project modal. Project deletion is intentionally not exposed in the UI —
// requires direct API call (DELETE /api/projects/:id) so it can't happen by
// accident. A delete cascades through ~14 tables, so the friction is wanted.
//
// Form structure:
//   Address block (address, city, state, zip)
//   Phase selector — drives which financial fields are visible + required
//   Primary client (first/last/email/phone)
//   Optional secondary client (toggle)
//   Auto-derived project name preview + override link
//   Phase-conditional fields (financial, dates) revealed when phase changes

import { useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Project } from '../types'
import { generateProjectName } from '../lib/project-name'

const US_STATES: Array<{ code: string; name: string }> = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
].map(([code, name]) => ({ code, name }))

const ZIP_RE = /^\d{5}(-\d{4})?$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function formatPhone(v: string): string {
  const digits = v.replace(/\D/g, '').slice(0, 10)
  if (digits.length < 4) return digits
  if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

type Phase = 'planning' | 'pre-construction' | 'active'

type Draft = {
  address: string
  city: string
  state: string
  zip: string
  phase: Phase
  client_first_name: string
  client_last_name: string
  client_email: string
  client_phone: string
  has_client2: boolean
  client2_first_name: string
  client2_last_name: string
  client2_email: string
  client2_phone: string
  name_override: string | null // null when auto-derived; string when user edited
  // Phase-conditional fields
  construction_loan_amount: string
  heritage_gc_fee: string
  lender_name: string
  title_company_name: string
  target_groundbreak_date: string
  start_date: string
}

const EMPTY: Draft = {
  address: '',
  city: '',
  state: 'MN',
  zip: '',
  phase: 'planning',
  client_first_name: '',
  client_last_name: '',
  client_email: '',
  client_phone: '',
  has_client2: false,
  client2_first_name: '',
  client2_last_name: '',
  client2_email: '',
  client2_phone: '',
  name_override: null,
  construction_loan_amount: '',
  heritage_gc_fee: '',
  lender_name: 'Deerwood Bank',
  title_company_name: 'DCA Title',
  target_groundbreak_date: '',
  start_date: '',
}

export default function NewProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (p: Project) => void
}) {
  const [d, setD] = useState<Draft>(EMPTY)
  const [editingName, setEditingName] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function update<K extends keyof Draft>(k: K, v: Draft[K]) {
    setD((prev) => ({ ...prev, [k]: v }))
  }

  const autoName = useMemo(
    () =>
      generateProjectName(
        { first: d.client_first_name, last: d.client_last_name },
        d.has_client2 ? { first: d.client2_first_name, last: d.client2_last_name } : null
      ),
    [d.client_first_name, d.client_last_name, d.has_client2, d.client2_first_name, d.client2_last_name]
  )
  const effectiveName = d.name_override ?? autoName ?? ''

  function validate(): string | null {
    if (!d.address.trim()) return 'Address is required'
    if (!d.city.trim()) return 'City is required'
    if (!d.state) return 'State is required'
    if (!d.zip.trim()) return 'ZIP is required'
    if (!ZIP_RE.test(d.zip.trim())) return 'ZIP must be 5 digits or 5+4 (e.g. 55001 or 55001-1234)'
    if (!d.client_first_name.trim() || !d.client_last_name.trim()) {
      return 'Primary client first + last name required'
    }
    if (d.client_email.trim() && !EMAIL_RE.test(d.client_email.trim())) {
      return 'Primary client email format is invalid'
    }
    if (d.has_client2) {
      if (d.client2_email.trim() && !EMAIL_RE.test(d.client2_email.trim())) {
        return 'Secondary client email format is invalid'
      }
    }
    if (d.phase === 'pre-construction' || d.phase === 'active') {
      if (!d.construction_loan_amount) return 'Construction loan amount required (pre-construction+)'
      if (!d.heritage_gc_fee) return 'Heritage GC fee required (pre-construction+)'
      if (!d.lender_name.trim()) return 'Lender required (pre-construction+)'
      if (!d.title_company_name.trim()) return 'Title company required (pre-construction+)'
      if (!d.target_groundbreak_date) return 'Target ground-break date required (pre-construction+)'
    }
    if (d.phase === 'active') {
      if (!d.start_date) return 'Actual start date required (active phase)'
    }
    return null
  }

  async function save() {
    const v = validate()
    if (v) {
      setErr(v)
      return
    }
    setSaving(true)
    setErr(null)
    try {
      const body: Partial<Project> & Record<string, unknown> = {
        address: d.address.trim(),
        city: d.city.trim(),
        state: d.state,
        zip: d.zip.trim(),
        status: d.phase,
        client_first_name: d.client_first_name.trim(),
        client_last_name: d.client_last_name.trim(),
        client_email: d.client_email.trim() || null,
        client_phone: d.client_phone.trim() || null,
        // Keep client_name populated as a fallback for old code reads.
        client_name:
          d.has_client2 && d.client2_last_name.trim()
            ? `${d.client_first_name} ${d.client_last_name} & ${d.client2_first_name} ${d.client2_last_name}`.trim()
            : `${d.client_first_name} ${d.client_last_name}`.trim(),
        client2_first_name: d.has_client2 ? d.client2_first_name.trim() || null : null,
        client2_last_name: d.has_client2 ? d.client2_last_name.trim() || null : null,
        client2_email: d.has_client2 ? d.client2_email.trim() || null : null,
        client2_phone: d.has_client2 ? d.client2_phone.trim() || null : null,
      }
      if (d.name_override !== null) {
        body.name = d.name_override
        body.name_manually_set = 1
      }
      // Phase-conditional fields
      if (d.phase === 'pre-construction' || d.phase === 'active') {
        body.construction_loan_amount = parseFloat(d.construction_loan_amount)
        body.heritage_gc_fee = parseFloat(d.heritage_gc_fee)
        body.lender_name = d.lender_name.trim()
        body.title_company_name = d.title_company_name.trim()
        body.target_groundbreak_date = d.target_groundbreak_date
      }
      if (d.phase === 'active') {
        body.start_date = d.start_date
      }
      const created = await api.projects.create(body)
      onCreated(created)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const showPreCon = d.phase === 'pre-construction' || d.phase === 'active'
  const showActive = d.phase === 'active'

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>New project</h3>

        {err && <div className="card alert mb-3">{err}</div>}

        {/* Address block */}
        <label className="form-row">
          <span>Address</span>
          <input
            autoFocus
            value={d.address}
            onChange={(e) => update('address', e.target.value)}
            placeholder="5855 Oakridge Trail S"
          />
        </label>
        <div className="form-grid-2">
          <label>
            <span className="form-label">City</span>
            <input value={d.city} onChange={(e) => update('city', e.target.value)} />
          </label>
          <label>
            <span className="form-label">State</span>
            <select value={d.state} onChange={(e) => update('state', e.target.value)}>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="form-row">
          <span>ZIP</span>
          <input
            value={d.zip}
            onChange={(e) => update('zip', e.target.value)}
            placeholder="55001 or 55001-1234"
            inputMode="numeric"
          />
        </label>

        {/* Phase selector */}
        <label className="form-row">
          <span>Phase</span>
          <select
            value={d.phase}
            onChange={(e) => update('phase', e.target.value as Phase)}
          >
            <option value="planning">Planning</option>
            <option value="pre-construction">Pre-construction</option>
            <option value="active">Active</option>
          </select>
        </label>

        {/* Primary client */}
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>Primary client</h4>
        <div className="form-grid-2">
          <label>
            <span className="form-label">First name</span>
            <input
              value={d.client_first_name}
              onChange={(e) => update('client_first_name', e.target.value)}
            />
          </label>
          <label>
            <span className="form-label">Last name</span>
            <input
              value={d.client_last_name}
              onChange={(e) => update('client_last_name', e.target.value)}
            />
          </label>
        </div>
        <div className="form-grid-2">
          <label>
            <span className="form-label">Email</span>
            <input
              type="email"
              value={d.client_email}
              onChange={(e) => update('client_email', e.target.value)}
            />
          </label>
          <label>
            <span className="form-label">Phone</span>
            <input
              type="tel"
              value={d.client_phone}
              onChange={(e) => update('client_phone', e.target.value)}
              onBlur={(e) => update('client_phone', formatPhone(e.target.value))}
              placeholder="651-555-0100"
            />
          </label>
        </div>

        {/* Secondary client toggle */}
        {!d.has_client2 ? (
          <button
            type="button"
            className="link mt-2"
            onClick={() => update('has_client2', true)}
          >
            + Add second client
          </button>
        ) : (
          <>
            <h4 style={{ marginTop: 16, marginBottom: 8 }}>
              Secondary client{' '}
              <button
                type="button"
                className="link"
                style={{ fontSize: '0.8rem', fontWeight: 'normal' }}
                onClick={() => update('has_client2', false)}
              >
                (remove)
              </button>
            </h4>
            <div className="form-grid-2">
              <label>
                <span className="form-label">First name</span>
                <input
                  value={d.client2_first_name}
                  onChange={(e) => update('client2_first_name', e.target.value)}
                />
              </label>
              <label>
                <span className="form-label">Last name</span>
                <input
                  value={d.client2_last_name}
                  onChange={(e) => update('client2_last_name', e.target.value)}
                />
              </label>
            </div>
            <div className="form-grid-2">
              <label>
                <span className="form-label">Email</span>
                <input
                  type="email"
                  value={d.client2_email}
                  onChange={(e) => update('client2_email', e.target.value)}
                />
              </label>
              <label>
                <span className="form-label">Phone</span>
                <input
                  type="tel"
                  value={d.client2_phone}
                  onChange={(e) => update('client2_phone', e.target.value)}
                  onBlur={(e) => update('client2_phone', formatPhone(e.target.value))}
                  placeholder="651-555-0101"
                />
              </label>
            </div>
          </>
        )}

        {/* Auto-name preview + override */}
        {effectiveName && (
          <div className="muted small mt-2">
            Project name:{' '}
            {editingName ? (
              <input
                style={{ fontSize: '0.85rem', padding: '2px 6px' }}
                value={d.name_override ?? autoName ?? ''}
                onChange={(e) => update('name_override', e.target.value)}
                onBlur={() => setEditingName(false)}
                autoFocus
              />
            ) : (
              <strong style={{ color: 'var(--gold)' }}>{effectiveName}</strong>
            )}
            {!editingName && (
              <button
                type="button"
                className="link"
                style={{ marginLeft: 8, fontSize: '0.75rem' }}
                onClick={() => setEditingName(true)}
              >
                Edit name
              </button>
            )}
          </div>
        )}

        {/* Phase-conditional fields, fade in on phase change */}
        <div
          style={{
            maxHeight: showPreCon ? '500px' : '0px',
            opacity: showPreCon ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.3s ease, opacity 0.25s ease',
          }}
        >
          <h4 style={{ marginTop: 16, marginBottom: 8 }}>Pre-construction details</h4>
          <div className="form-grid-2">
            <label>
              <span className="form-label">Construction loan ($)</span>
              <input
                type="number"
                value={d.construction_loan_amount}
                onChange={(e) => update('construction_loan_amount', e.target.value)}
              />
            </label>
            <label>
              <span className="form-label">Heritage GC fee ($)</span>
              <input
                type="number"
                value={d.heritage_gc_fee}
                onChange={(e) => update('heritage_gc_fee', e.target.value)}
              />
            </label>
          </div>
          <div className="form-grid-2">
            <label>
              <span className="form-label">Lender</span>
              <input
                value={d.lender_name}
                onChange={(e) => update('lender_name', e.target.value)}
              />
            </label>
            <label>
              <span className="form-label">Title company</span>
              <input
                value={d.title_company_name}
                onChange={(e) => update('title_company_name', e.target.value)}
              />
            </label>
          </div>
          <label className="form-row">
            <span>Target ground-break date</span>
            <input
              type="date"
              value={d.target_groundbreak_date}
              onChange={(e) => update('target_groundbreak_date', e.target.value)}
            />
          </label>
        </div>

        <div
          style={{
            maxHeight: showActive ? '120px' : '0px',
            opacity: showActive ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.3s ease, opacity 0.25s ease',
          }}
        >
          <label className="form-row">
            <span>Actual start date</span>
            <input
              type="date"
              value={d.start_date}
              onChange={(e) => update('start_date', e.target.value)}
            />
          </label>
        </div>

        <div className="row gap-2 mt-3">
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Create project'}
          </button>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
